import Svg from "components/common/SVG/Svg";
import { IUser } from "core/types/base/IUser";
import React from "react";
import { Column } from "react-table";
import DropdownPortal from "../features/DropdownPortal";
import { GlobalAdminRole } from "./helpers/isGlobalAdmin";

/**
 * Table columns configuration for admin management
 *
 * Features:
 * - Automatic dropdown closing after actions for better UX
 * - Controlled state management for dropdown visibility
 * - Reusable DropdownAction component for consistent behavior
 * - Independent dropdown state for each table row
 * - Optimized with React.useMemo to prevent unnecessary re-renders
 *
 * @param handleDropdownAction - Function to handle dropdown action clicks
 * @returns Array of table column configurations optimized for performance
 */

type TProps = {
  handleDropdownAction: (
    row: IUser,
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    modalKey: string,
  ) => void;
};

/**
 * Wrapper component for dropdown actions that automatically closes the dropdown
 *
 * This component ensures better UX by:
 * - Executing the provided onClick handler
 * - Automatically closing the dropdown after action execution
 * - Maintaining consistent behavior across all dropdown actions
 * - Preventing dropdown from staying open after user interaction
 *
 * @param children - The content to display inside the action link
 * @param onClick - The action handler function to execute
 * @param onClose - Function to close the parent dropdown
 */
const DropdownAction: React.FC<{
  children: React.ReactNode;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onClose: () => void;
}> = ({ children, onClick, onClose }) => (
  <a
    className="dropdown-item"
    href="!#"
    onClick={event => {
      onClick(event);
      onClose(); // Automatically close dropdown after action
    }}
  >
    {children}
  </a>
);

const AdminsManagementTableColumns = ({ handleDropdownAction }: TProps) => {
  /**
   * Determines the status text based on user's active state and registration completion
   * @param isActive - Whether the user account is active
   * @param registrationCompleted - Whether the user has completed registration
   * @returns Status text: "Active", "Pending", or "Inactive"
   */
  const checkStatus = (isActive: boolean, registrationCompleted: boolean): string => {
    // eslint-disable-next-line no-nested-ternary
    return isActive ? (registrationCompleted ? "Active" : "Pending") : "Inactive";
  };

  /**
   * Determines the CSS class for status badge styling
   * @param isActive - Whether the user account is active
   * @param registrationCompleted - Whether the user has completed registration
   * @returns CSS class: "success", "warning", or "danger"
   */
  const checkLabelStatus = (isActive: boolean, registrationCompleted: boolean): string => {
    // eslint-disable-next-line no-nested-ternary
    return isActive ? (registrationCompleted ? "success" : "warning") : "danger";
  };

  return React.useMemo(
    () =>
      [
        {
          Header: "Full Name",
          accessor: "firstName",
          disableSortBy: false,
          Cell: ({
            row: {
              original: { firstName, lastName },
            },
          }: any) => `${firstName} ${lastName}`,
        },
        {
          Header: "Email",
          accessor: "email",
          disableSortBy: false,
        },
        {
          Header: "Phone",
          accessor: "phoneNumber",
          disableSortBy: false,
          Cell: ({
            row: {
              original: { phoneNumber },
            },
          }: any) => phoneNumber || "--",
        },
        {
          Header: "Role",
          accessor: "roles",
          disableSortBy: true,
          Cell: ({
            row: {
              original: { roles, firstName, lastName },
            },
          }: any) => {
            // Log roles for debugging
            // eslint-disable-next-line no-console
            console.log(`User ${firstName} ${lastName} roles:`, roles);

            // Check if user has Global Admin role
            if (roles?.includes(GlobalAdminRole)) {
              return "Global Admin";
            }

            // Display all roles from the roles array
            if (roles && Array.isArray(roles) && roles.length > 0) {
              return roles.join(", ");
            }

            return "--";
          },
        },
        {
          Header: "Status",
          accessor: "status",
          disableSortBy: true,
          Cell: ({
            row: {
              original: { isActive, registrationCompleted },
            },
          }: any) => (
            <div className="d-flex flex-column align-items-center ">
              <span className={`badge bg-${checkLabelStatus(isActive, registrationCompleted)}`}>
                {checkStatus(isActive, registrationCompleted)}
              </span>
            </div>
          ),
        },
        /**
         * Actions column with dropdown menu for user management
         * Features automatic dropdown closing after each action for better UX
         */
        {
          Header: "Actions",
          accessor: "actions",
          disableSortBy: true,
          Cell: ({
            row: {
              original,
              original: { isActive },
            },
          }: any) => {
            // State to control dropdown visibility for each row
            // Each row has its own dropdown state for independent control
            const [isOpen, setIsOpen] = React.useState(false);

            return (
              <DropdownPortal
                button={
                  <button type="button" className="btn btn-primary btn-sm">
                    {Svg({ icon: "threeDots", width: 20, height: 20 })}
                  </button>
                }
                isOpen={isOpen}
                onToggle={setIsOpen}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {/* Each action automatically closes the dropdown after execution */}
                  {/* This provides better UX by not keeping the dropdown open after action */}
                  <DropdownAction
                    onClick={event => handleDropdownAction(original, event, "editUser")}
                    onClose={() => setIsOpen(false)}
                  >
                    Edit
                  </DropdownAction>
                  <DropdownAction
                    onClick={event => handleDropdownAction(original, event, "warningStatus")}
                    onClose={() => setIsOpen(false)}
                  >
                    {isActive ? "Deactivate" : "Activate"}
                  </DropdownAction>
                  <DropdownAction
                    onClick={event => handleDropdownAction(original, event, "warningPassword")}
                    onClose={() => setIsOpen(false)}
                  >
                    Reset Password
                  </DropdownAction>
                  <DropdownAction
                    onClick={event => handleDropdownAction(original, event, "changeEmail")}
                    onClose={() => setIsOpen(false)}
                  >
                    Change Email
                  </DropdownAction>
                </div>
              </DropdownPortal>
            );
          },
        },
      ] as Column<object>[],
    [handleDropdownAction],
  );
};

export default AdminsManagementTableColumns;
